import { useRef, useCallback } from 'react';

interface SwipeState {
  startY: number;
  startX: number;
  startTime: number;
}

interface UseSwipeToCloseOptions {
  /** Minimum vertical distance (px) to trigger close. Default: 72 */
  threshold?: number;
  /** Maximum horizontal drift (px) allowed to still count as vertical swipe. Default: 40 */
  maxHorizontalDrift?: number;
  /** Maximum duration (ms) for a "flick" gesture. Default: 300 */
  flickMs?: number;
  /** Minimum velocity (px/ms) to trigger close on a flick. Default: 0.5 */
  flickVelocity?: number;
}

interface UseSwipeToCloseReturn {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
}

/**
 * Custom hook that detects a downward swipe gesture and calls `onClose`.
 *
 * Handles two patterns:
 * 1. **Slow drag** – swipe distance >= `threshold` px downward.
 * 2. **Quick flick** – fast downward flick with velocity >= `flickVelocity` px/ms,
 *    even if total distance is less than `threshold`.
 *
 * The hook does NOT attach any event listener to the DOM directly; instead it
 * returns the three event handlers to be spread onto a React element.
 */
function useSwipeToClose(
  onClose: () => void,
  {
    threshold = 72,
    maxHorizontalDrift = 40,
    flickMs = 300,
    flickVelocity = 0.5,
  }: UseSwipeToCloseOptions = {},
): UseSwipeToCloseReturn {
  const swipeRef = useRef<SwipeState | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    swipeRef.current = {
      startY: touch.clientY,
      startX: touch.clientX,
      startTime: performance.now(),
    };
  }, []);

  const onTouchMove = useCallback((_e: React.TouchEvent) => {
    // Reserved for future: live drag visual feedback (translateY on container)
  }, []);

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const state = swipeRef.current;
      swipeRef.current = null;
      if (!state) return;

      const touch = e.changedTouches[0];
      if (!touch) return;

      const deltaY = touch.clientY - state.startY;
      const deltaX = Math.abs(touch.clientX - state.startX);
      const elapsed = performance.now() - state.startTime;

      // Must be predominantly vertical
      if (deltaX > maxHorizontalDrift) return;
      // Must be going downward
      if (deltaY <= 0) return;

      const velocity = deltaY / elapsed;

      const isSlow = deltaY >= threshold;
      const isFlick = elapsed <= flickMs && velocity >= flickVelocity;

      if (isSlow || isFlick) {
        onClose();
      }
    },
    [onClose, threshold, maxHorizontalDrift, flickMs, flickVelocity],
  );

  return { onTouchStart, onTouchMove, onTouchEnd };
}

export default useSwipeToClose;
